import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface EditLibraryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  library: { id: string; name: string; description: string } | null;
  onSave?: (libraryId: string, body: { name: string; description?: string }) => Promise<void>;
}

export function EditLibraryDialog({
  open,
  onOpenChange,
  library,
  onSave,
}: EditLibraryDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && library) {
      setName(library.name);
      setDescription(library.description);
      setError(null);
    }
  }, [open, library]);

  async function handleSave() {
    if (!name.trim() || !library || !onSave) return;

    setSaving(true);
    setError(null);
    try {
      await onSave(library.id, {
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update library");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-glass-border bg-[#111b35] text-white sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Edit Library</DialogTitle>
          <DialogDescription className={`text-slate-400 ${pageDescriptionMobileHiddenClassName}`}>
            Update the library name and description.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="edit-library-name" className="text-slate-400">
              Name
            </Label>
            <Input
              id="edit-library-name"
              placeholder="e.g. Contracts 2024"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-library-desc" className="text-slate-400">
              Description (optional)
            </Label>
            <Input
              id="edit-library-desc"
              placeholder="What documents belong here?"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-red-400">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="default" onClick={() => void handleSave()} disabled={saving || !name.trim()} className="gap-2">
            <Save className="h-4 w-4" />
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
